import * as _ from 'lodash';

import { EmbedActions } from '@mode/reports/sandbox/contract';

// This holds the list of subscribers for each message
interface Subscriptions {
  [s: string]: Array<Subscription>;
}

interface Subscription {
  handler: (msg) => any;
  context: any;
}

export interface WindowMessagePayload {
  type: MSG_TYPES;
  [index: string]: any;
}

export enum MSG_TYPES {
  GetNewSize = 'getNewSize',

  SelectTableColumn = 'selectTableColumn',

  EnterFullScreen = 'enterFullScreen',
  ExitFullScreen = 'exitFullScreen',

  UpdateFeatureState = 'updateFeatureState',
}

export class WindowMessageService {
  static MSG_TYPES = MSG_TYPES;

  private subscriptions: Subscriptions = {};

  static $inject = ['$window'];

  public constructor(private $window: Window) {
    $window.addEventListener('message', this.handleMessage);
  }

  handleMessage = (msg: any) => {
    if (msg.data.type) {
      if (this.subscriptions[msg.data.type]) {
        this.subscriptions[msg.data.type].forEach(function (sub) {
          sub.handler.call(sub.context, msg.data);
        });
      }

      if (this.subscriptions['*']) {
        this.subscriptions['*'].forEach(function (sub) {
          sub.handler.call(sub.context, msg.data);
        });
      }
    }
  };

  private postMessageToParent(msg: any, origin = '*', ports?: any) {
    this.$window.parent.postMessage(msg, origin);
  }

  private postMessageToFrame(msg: any, origin = '*', ports?: any) {
    const frame = this.$window.frames['mode-embed-frame'];
    if (frame) {
      frame.postMessage(msg, origin);
    }
  }

  public postMessage(msg: any, origin = '*', ports?: any) {
    if (this.$window.frames['mode-embed-frame']) {
      this.postMessageToFrame(msg, origin, ports);
    } else {
      this.postMessageToParent(msg, origin, ports);
    }
  }

  /**
   *
   * @param handler The event callback
   * @param type Message type to subscribe to. If omitted, handler will receive all message types.
   * @param context Context for the callback
   * @returns true if handler does not exist and was subscribed, false otherwise
   */
  public subscribe(context: any, handler: (msg: EmbedActions | WindowMessagePayload) => void, type = '*') {
    if (this.subscriptions[type] === undefined) {
      this.subscriptions[type] = [];
    }

    const sub = _.find(this.subscriptions[type], { handler, context });
    if (!sub) {
      this.subscriptions[type].push({ handler, context });
      return true;
    }

    return false;
  }

  /**
   * Unsubscribes from post message events. Handler and type are optional. If handler is not provided,
   * then all handlers belonging to context will be unsubscribed. If type is not provided, then all types
   * (all types PLUS '*') will be unsubscribed.
   *
   * @param handler Callback to unsubscribe. If not provided, unsubscribe context from all subs.
   * @param type Message type that was originally subscribed to. If not provided, then unsub '*' and all other types.
   * @param context Context for the callback
   * @returns true if one or more subscriptions have been removed
   */
  public unsubscribe(context: any, handler?: (msg) => any, type?: string): boolean {
    let typesToRemove = [type];

    if (!type) {
      typesToRemove = _.keys(this.subscriptions);
    }

    let removed = false;
    typesToRemove.forEach((t) => {
      if (this.subscriptions[t]) {
        const sub = _.find(this.subscriptions[t], handler ? { handler, context } : { context });
        if (sub) {
          this.subscriptions[t].splice(this.subscriptions[t].indexOf(sub), 1);
          removed = true;
        }
      }
    });

    return removed;
  }

  public selectTableColumn(tableId: string, column: string) {
    this.postMessage({
      type: MSG_TYPES.SelectTableColumn,
      data: {
        id: tableId,
        column,
      },
    });
  }

  public EnterFullScreen() {
    this.postMessage({
      type: MSG_TYPES.EnterFullScreen,
    });
  }

  public ExitFullScreen() {
    this.postMessage({
      type: MSG_TYPES.ExitFullScreen,
    });
  }

  public updateFeatureState(state) {
    this.postMessageToFrame({
      type: MSG_TYPES.UpdateFeatureState,
      state,
    });
  }
}
